import { create } from 'zustand';
import axios from 'axios';
import { API_integrate_square } from '../constants';
import { getDefaultAxiosConfig } from '../main/content/requests/utilities/requests';

const useIntegratePOSSquareStore = create((set, get) => ({
  // Square-specific form data
  formData: {
    // Prerequisites (Step 0)
    squareAccountVerified: false,

    // App Setup (Step 1)
    appSetupCompleted: false,

    // API Credential Values (Step 2)
    applicationId: '',
    accessToken: '',
  },

  // Current step/page
  currentStep: 0,

  // Total steps for Square integration
  totalSteps: 3, // Prerequisites, App Setup, Credential Input

  // Actions
  setFormData: (field, value) => set((state) => ({
    formData: {
      ...state.formData,
      [field]: value,
    },
  })),

  // Step 0: Prerequisites validation
  setSquareAccountVerified: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      squareAccountVerified: completed,
    },
  })),

  // Step 1: App setup validation
  setAppSetupCompleted: (completed) => set((state) => ({
    formData: {
      ...state.formData,
      appSetupCompleted: completed,
    },
  })),

  // Navigation
  nextStep: () => set((state) => ({
    currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1),
  })),

  previousStep: () => set((state) => ({
    currentStep: Math.max(state.currentStep - 1, 0),
  })),

  resetForm: () => set(() => ({
    formData: {
      squareAccountVerified: false,
      appSetupCompleted: false,
      applicationId: '',
      accessToken: '',
    },
    currentStep: 0,
  })),

  // Validation helpers
  isStepValid: (step) => {
    const { formData } = get();
    switch (step) {
      case 0: // Prerequisites check
        return formData.squareAccountVerified;
      case 1: // App setup
        return formData.appSetupCompleted;
      case 2: // Credential input
        return (
          formData.applicationId.trim().length > 0 &&
          formData.accessToken.trim().length > 0
        );
      default:
        return false;
    }
  },

  // Square integration function
  integrateSquare: async (company_id) => {
    try {
      const { formData, resetForm } = get();

      console.log('Square integration completed!');

      // Get axios config with proper authentication
      const axiosConfig = await getDefaultAxiosConfig();

      // Make API call to your backend to save the Square credentials
      const response = await axios.post(API_integrate_square, {
        company_id,
        integrationType: 'api',
        application_id: formData.applicationId,
        access_token: formData.accessToken,
        createdAt: new Date().toISOString(),
        version: '1.0'
      }, axiosConfig);

      const connectionSuccessful = Boolean(
        response?.data?.redux?.payload?.connection_successful
      );

      if (!connectionSuccessful) {
        return {
          success: false,
          error: "POS authentication failed. Please verify your credentials and try again.",
        };
      }

      // Reset the form after successful integration
      resetForm();

      return {
        success: true,
        data: {
          pos: 'square',
          integrationType: 'api'
        }
      };

    } catch (error) {
      console.error('Error integrating Square:', error);

      const serverMessage = error?.response?.data?.system?.message;

      return {
        success: false,
        error: serverMessage || error.message || 'Failed to integrate Square. Please try again.'
      };
    }
  },
}));

export default useIntegratePOSSquareStore;
